import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import type { AdminHelpRequest, HelpRequestStatus } from '@/help/schemas'

export const HELP_REQUESTS_QUERY_KEY = ['admin', 'help-requests'] as const

export interface HelpRequestsFilter {
  status?: HelpRequestStatus
  /** A user kérései bejelentőként VAGY érintettként (DEC-ADMIN-015 kieg.). */
  user?: string
  /** Bejelentő VAGY érintett becenév-részlet. */
  name?: string
  contextPage?: string
  category?: string
  createdFrom?: string
  createdTo?: string
  page?: number
}

export interface HelpRequestsPageMeta {
  current_page: number
  last_page: number
  total: number
}

export interface HelpRequestsPage {
  items: AdminHelpRequest[]
  meta: HelpRequestsPageMeta
}

/**
 * `GET /admin/help-requests` (DEC-SUPPORT-001 / DEC-ADMIN-015 kieg.).
 *
 * Paginated list; szűrők: status, user (ULID), name (becenév-részlet),
 * context_page, category, created_from/to + page. Default per_page=25, sort
 * `created_at DESC + id DESC`. A Laravel-envelope `data`+`meta` mezőit
 * manuálisan extracteljük (mint a users-listán).
 */
export function useHelpRequests(filter: HelpRequestsFilter = {}) {
  return useQuery({
    queryKey: [...HELP_REQUESTS_QUERY_KEY, filter] as const,
    queryFn: async (): Promise<HelpRequestsPage> => {
      const query: Record<string, string | number> = {}
      if (filter.status) query.status = filter.status
      if (filter.user) query.user = filter.user
      if (filter.name) query.name = filter.name
      if (filter.contextPage) query.context_page = filter.contextPage
      if (filter.category) query.category = filter.category
      if (filter.createdFrom) query.created_from = filter.createdFrom
      if (filter.createdTo) query.created_to = filter.createdTo
      if (filter.page && filter.page > 1) query.page = filter.page
      const { data, error } = await apiClient.GET('/admin/help-requests', {
        params: Object.keys(query).length > 0 ? { query } : undefined,
      })
      if (error) throw error
      const envelope = data as
        | { data?: AdminHelpRequest[]; meta?: Partial<HelpRequestsPageMeta> }
        | undefined
      const items = envelope?.data ?? []
      return {
        items,
        meta: {
          current_page: envelope?.meta?.current_page ?? 1,
          last_page: envelope?.meta?.last_page ?? 1,
          total: envelope?.meta?.total ?? items.length,
        },
      }
    },
  })
}

interface UpdateHelpRequestVariables {
  id: number
  status: HelpRequestStatus
}

/**
 * `PATCH /admin/help-requests/{id}` (DEC-SUPPORT-001).
 *
 * Status flip; resolved / dismissed are terminal (the backend stamps
 * handled_by / handled_at + writes an audit_logs row).
 */
export function useUpdateHelpRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, status }: UpdateHelpRequestVariables): Promise<void> => {
      const { error } = await apiClient.PATCH('/admin/help-requests/{id}', {
        params: { path: { id } },
        body: { status },
      })
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: HELP_REQUESTS_QUERY_KEY })
    },
  })
}
